import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { PAGE_TITLES } from "~/configs/pageTitles";
import { canMember, extractAuthorizations } from "~/helpers/authorizations";
import { useKeywordDetailPageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import { apiClient } from "../../api/ApiClient";
import Keyword from "../../components/keyword/Keyword";
import Button from "../../components/mantine/button/Button";
import Breadcrumbs from "../../components/ui/breadcrumbs/Breadcrumbs";
import Spinner from "../../components/ui/spinner/Spinner";
import { useSiteContext } from "../../contexts/SiteContext";
import { useSetFocusKeyword } from "../../hooks/useSetFocusKeyword";
import styles from "./KeywordPage.module.scss";

const KeywordPage = () => {
  const { id, keywordId } = useParams();
  const siteContext = useSiteContext();

  if (!id) {
    throw Error("No site id provided");
  }

  if (!keywordId) {
    throw Error("No keyword id provided");
  }

  const { data: keywordData, isLoading: isKeywordLoading } = useQuery({
    queryKey: ["keyword", id, keywordId],
    queryFn: async () => {
      const response = await apiClient.sites.keywordsDetail2(
        Number(id),
        Number(keywordId),
        { headers: { "Authorize-Member-Actions": "focus" } },
      );

      return {
        keyword: response?.data?.keyword,
        authorizations: extractAuthorizations(response?.data),
      };
    },
    enabled: !!id || !!keywordId,
  });
  const keyword = keywordData?.keyword;
  const {
    data: keywordHistoricalRanking,
    isLoading: isLoadingHistoricalRanking,
  } = useQuery({
    queryKey: ["keyword_historical_ranking", id, keywordId],
    queryFn: async () => {
      const response = await apiClient.sites.keywordsHistoricalRankingDetail(
        Number(id),
        Number(keywordId),
      );

      return response?.data?.keyword_historical_ranking;
    },
    enabled: !!id || !!keywordId,
  });
  const { data: keywordSearchVolume, isLoading: isLoadingKeywordSearchVolume } =
    useQuery({
      queryKey: ["keyword_ranking", id, keywordId],
      queryFn: async () => {
        const response = await apiClient.sites.keywordsMonthlySearchesDetail(
          Number(id),
          Number(keywordId),
          {
            "page[size]": 12,
            s: "year DESC, month  DESC",
          },
        );

        return response?.data?.keyword_monthly_searches;
      },
      enabled: !!id || !!keywordId,
    });

  const hasFocusPermission = canMember(keywordData?.authorizations, "focus");
  const isFocusKeyword =
    siteContext?.site?.site?.focus_keyword_id === Number(keywordId);

  const { mutate: setFocusKeyword, isPending: isPendingSetFocusKeyword } =
    useSetFocusKeyword({ siteId: Number(id) });

  // Set the page title with the enhanced pattern
  useKeywordDetailPageTitle(keyword?.name);

  const fakeBreadcrumbs = [
    {
      title: PAGE_TITLES.keywordsList,
      link: routes.site.keywords.list({ id }),
    },
    {
      title: keyword?.name || "",
      link: routes.site.keywords.keyword({ id, keywordId }),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.wrapper}>
        <div>
          <h1 className={styles.title}>Keyword: {keyword?.name}</h1>
          {fakeBreadcrumbs?.length && (
            <Breadcrumbs fakeBreadcrumbs={fakeBreadcrumbs} />
          )}
        </div>
        {keyword && hasFocusPermission && (
          <div className={styles.headerActions}>
            <Button
              variant={isFocusKeyword ? "outlinedIcon" : "default"}
              onClick={() => setFocusKeyword(Number(keywordId))}
              disabled={isFocusKeyword || isPendingSetFocusKeyword}
            >
              <span>
                {isPendingSetFocusKeyword && <Spinner size="1rem" />}
                {isFocusKeyword ? "Focus Keyword" : "Set Focus Keyword"}
              </span>
            </Button>
          </div>
        )}
      </div>
      {keyword && (
        <Keyword
          data={keyword}
          isLoading={isKeywordLoading}
          keywordHistoricalRanking={keywordHistoricalRanking || null}
          keywordSearchVolume={keywordSearchVolume || null}
          isLoadingHistoricalRanking={isLoadingHistoricalRanking}
          isLoadingKeywordSearchVolume={isLoadingKeywordSearchVolume}
        />
      )}
    </div>
  );
};
export default KeywordPage;
